import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import Cookies from 'js-cookie';

import { AirportSearchViewModel } from '@api/index';
import { DateType } from '@utils/dateUtil';
import {
  FlightGuestProps,
  FlightSearchBarProps,
  FlightTripType,
} from '@components/views/flight/types';
import { initialGuestInfo } from '@components/views/flight/constants';
import { validateFormField } from '@components/views/flight/utils';

interface Props {
  flightTripType?: FlightTripType;
  startDate?: DateType | null;
  endDate?: DateType | null;
  departureAirportSearchText?: string;
  departureAirport?: AirportSearchViewModel;
  arrivalAirportSearchText?: string;
  arrivalAirport?: AirportSearchViewModel;
  guestInfo?: FlightGuestProps;
  isShowModal?: boolean;
  package?: boolean;
}

const useSearchBar = (
  props?: Props
): Omit<FlightSearchBarProps, 'onResearch'> => {
  const [startDate, setStartDate] = useState<DateType | null>(null);
  const [endDate, setEndDate] = useState<DateType | null>(null);
  const [arrivalAirportSearchText, setArrivalAirportSearchText] =
    useState<string>();
  const [departureAirportSearchText, setDepartureAirportSearchText] =
    useState<string>();
  const [departureAirport, setDepartureAirport] =
    useState<AirportSearchViewModel>();
  const [arrivalAirport, setArrivalAirport] =
    useState<AirportSearchViewModel>();
  const [guestInfo, setGuestInfo] =
    useState<FlightGuestProps>(initialGuestInfo);
  const [flightTripType, setFlightTripType] = useState<FlightTripType>();

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      flightTripType: validateFormField(flightTripType) as string,
      arrivalAirport: validateFormField(arrivalAirport) as string,
      departureAirport: validateFormField(departureAirport) as string,
      startDate: validateFormField(startDate) as string,
      endDate: validateFormField(endDate) as string,
      guestInfo: validateFormField(guestInfo) as string,
    },
  });

  useEffect(() => {
    if (props?.departureAirport) {
      setDepartureAirport(props.departureAirport);
      setDepartureAirportSearchText(
        `${props.departureAirport.airportName}, ${props.departureAirport.airportCode}`
      );
      setValue(
        'departureAirport',
        validateFormField(props.departureAirport) as string
      );
    } else {
      const stored = Cookies.get('departurePackage');
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as AirportSearchViewModel;
          setDepartureAirport(parsed);
          setDepartureAirportSearchText(
            `${parsed.airportName}, ${parsed.airportCode}`
          );
          setValue('departureAirport', validateFormField(parsed) as string);
        } catch (err) {
          console.error('departurePackage cookie verisi hatalı:', err);
        }
      }
    }
  }, [props?.departureAirport]);

  useEffect(() => {
    if (props?.flightTripType) {
      setFlightTripType(props.flightTripType);
      setValue(
        'flightTripType',
        validateFormField(props.flightTripType) as string
      );
    }
  }, [props?.flightTripType, props?.isShowModal]);

  useEffect(() => {
    setStartDate(props?.startDate || null);
    setValue('startDate', validateFormField(props?.startDate) as string);
  }, [props?.startDate, props?.isShowModal]);

  useEffect(() => {
    setEndDate(props?.endDate || null);
    setValue('endDate', validateFormField(props?.endDate) as string);
  }, [props?.endDate, props?.isShowModal]);

  useEffect(() => {
    if (props?.departureAirportSearchText) {
      onDepartureAirportTextChange(props.departureAirportSearchText);
    }
  }, [props?.departureAirportSearchText, props?.isShowModal]);

  useEffect(() => {
    if (props?.arrivalAirportSearchText) {
      onArrivalAirportTextChange(props.arrivalAirportSearchText);
    }
  }, [props?.arrivalAirportSearchText, props?.isShowModal]);

  useEffect(() => {
    if (props?.arrivalAirport) {
      setArrivalAirport(props.arrivalAirport);
      setValue(
        'arrivalAirport',
        validateFormField(props.arrivalAirport) as string
      );
    }
  }, [props?.arrivalAirport, props?.isShowModal]);

  useEffect(() => {
    setGuestInfo(props?.guestInfo || initialGuestInfo);
    setValue(
      'guestInfo',
      validateFormField(props?.guestInfo || initialGuestInfo) as string
    );
  }, [props?.guestInfo, props?.isShowModal]);

  const onChangeFlightTripType = (tripType: FlightTripType) => {
    setFlightTripType(tripType);
    setValue('flightTripType', validateFormField(tripType) as string);
  };

  const onDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: DateType | null;
    endDate: DateType | null;
  }) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setValue('startDate', validateFormField(startDate) as string);
    setValue('endDate', validateFormField(endDate) as string);
  };

  const onDateChange = (date: DateType | null) => {
    setStartDate(date);
    setValue('startDate', validateFormField(date) as string);
  };

  const onDepartureAirportSelect = (
    item: AirportSearchViewModel | undefined
  ) => {
    if (item?.airportCode === arrivalAirport?.airportCode) {
      if (departureAirport) {
        setArrivalAirport(departureAirport);
        onArrivalAirportTextChange(
          `${departureAirport.airportName}, ${departureAirport.airportCode}`
        );
      } else {
        setArrivalAirport(undefined);
        onArrivalAirportTextChange('');
      }
    }

    setValue('departureAirport', validateFormField(item) as string);

    if (item) {
      onDepartureAirportTextChange(`${item.airportName}, ${item.airportCode}`);
      setDepartureAirport(item);
      if (props?.package) {
        Cookies.set('departurePackage', JSON.stringify(item));
      }
    }
  };

  const onArrivalAirportSelect = (item: AirportSearchViewModel | undefined) => {
    if (item?.airportCode === departureAirport?.airportCode) {
      if (arrivalAirport) {
        setDepartureAirport(arrivalAirport);
        onDepartureAirportTextChange(
          `${arrivalAirport.airportName}, ${arrivalAirport.airportCode}`
        );
      } else {
        setDepartureAirport(undefined);
        onDepartureAirportTextChange('');
      }
    }

    if (item) {
      onArrivalAirportTextChange(`${item.airportName}, ${item.airportCode}`);
      setArrivalAirport(item);
    }

    setValue('arrivalAirport', validateFormField(item) as string);
  };

  const onDepartureAirportTextChange = (text: string | undefined) => {
    setDepartureAirportSearchText(text);
    if (!text && !departureAirport) {
      setDepartureAirport(undefined);
      setValue('departureAirport', validateFormField(undefined) as string);
    }
  };

  const onArrivalAirportTextChange = (text: string | undefined) => {
    setArrivalAirportSearchText(text);
    if (!text && !arrivalAirport) {
      setArrivalAirport(undefined);
      setValue('arrivalAirport', validateFormField(undefined) as string);
    }
  };

  const onApplyGuestsForm = (guestInfoParam: FlightGuestProps | undefined) => {
    setGuestInfo(guestInfoParam || initialGuestInfo);
  };

  return {
    flightTripType,
    onChangeFlightTripType,
    startDate,
    endDate,
    onDatesChange,
    onDateChange,
    departureAirportSearchText,
    departureAirport,
    onDepartureAirportTextChange,
    onDepartureAirportSelect,
    arrivalAirportSearchText,
    arrivalAirport,
    onArrivalAirportTextChange,
    onArrivalAirportSelect,
    guestInfo,
    onApplyGuestsForm,
    handleSubmit,
    errors,
  };
};

export default useSearchBar;
